import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  CARS_BOOKED_STATE_ASSIGNED,
  CARS_BOOKED_STATE_BLIND_PLUS,
  CARS_BOOKED_STATE_NOT_ASSIGNED,
  isCarsBookedAssigned,
} from 'lib/carsBookedStates'
import { mapLocation } from 'lib/carDetailsLib'
import { addDays, isAfter, isBefore, parse, subDays } from 'date-fns'
import getYearMonthDayDate from 'lib/getYearMonthDayDate'
import { LOADER_ALL_STATES } from 'lib/loaderStates'

import {
  checkAutomaticAssignment,
  checkNoProposalAssignment,
  checkRecapChoice,
  checkTeamProposalAssignment,
} from 'components/organisms/Assignment/assignmentLib'

export const FILTER_CAPS_NONE_CHOICE = ''
export const FILTER_CAPS_FIRST_CHOICE = 'firstChoice'
export const FILTER_CAPS_SECOND_CHOICE = 'secondChoice'
export const FILTER_CAPS_ALL_CHOICES = 'allChoices'
export const FILTER_CAPS_NO_CHOICES = 'noChoices'
export const FILTER_CAPS_BLIND_OFFERS = 'blindOffers'

export const FILTER_ASSIGNMENT_CAPS_NONE_CHOICE = ''
export const FILTER_ASSIGNMENT_CAPS_FIRST_CHOICE = 'firstChoice'
export const FILTER_ASSIGNMENT_CAPS_SECOND_CHOICE = 'secondChoice'
export const FILTER_ASSIGNMENT_CAPS_BLIND_OFFERS = 'blindOffers'
export const FILTER_ASSIGNMENT_CAPS_NOT_CHOSEN = 'notChosen'

export const FILTER_ASSIGNMENT_TYPE_NONE_CHOICE = ''
export const FILTER_ASSIGNMENT_TYPE_AUTOMATIC_CHOICE = 'automatic'
export const FILTER_ASSIGNMENT_TYPE_TEAM_PROPOSAL_CHOICE = 'teamProposal'
export const FILTER_ASSIGNMENT_TYPE_NO_PROPOSAL_CHOICE = 'noProposal'

export const FILTER_CARS_BOOKED_TYPE_NONE_CHOICE = ''
export const FILTER_CARS_BOOKED_TYPE_FIRST_CHOICE = 'firstChoice'
export const FILTER_CARS_BOOKED_TYPE_SECOND_CHOICE = 'secondChoice'
export const FILTER_CARS_BOOKED_TYPE_ASSIGNED_CHOICE =
  CARS_BOOKED_STATE_ASSIGNED
export const FILTER_CARS_BOOKED_TYPE_NOT_ASSIGNED_CHOICE =
  CARS_BOOKED_STATE_NOT_ASSIGNED

const DATE_FORMAT = 'yyyy-MM-dd'

function hasCarsBookedBlindPlusChoice(car) {
  return (
    !!car?.isBlindSelection ||
    car?.choice === 'first' ||
    car?.assignedState === CARS_BOOKED_STATE_BLIND_PLUS ||
    (car?.blindPrice !== null &&
      car?.blindPrice !== undefined &&
      car?.blindPrice !== '') ||
    !!car?.secondChoice
  )
}

function getCarsBookedDisplayStatus(car) {
  return isCarsBookedAssigned(car)
    ? CARS_BOOKED_STATE_ASSIGNED
    : CARS_BOOKED_STATE_NOT_ASSIGNED
}

function filterKm(car, filters) {
  return !(car.km < filters.kmMin || car.km > filters.kmMax)
}

function filterDateRange(date, from, to) {
  const formattedFrom = subDays(
    parse(
      from,
      // t('common.date.format.formatDMYISO'),
      DATE_FORMAT,
      new Date()
    ),
    1
  )
  const formattedTo = addDays(
    parse(
      to,
      // t('common.date.format.formatDMYISO'),
      DATE_FORMAT,
      new Date()
    ),
    1
  )

  const formattedDate = date
    ? parse(getYearMonthDayDate(new Date(date)), DATE_FORMAT, new Date())
    : null

  const fromCheck =
    from !== null ? isBefore(formattedFrom, formattedDate) : true
  const toCheck = to !== null ? isAfter(formattedTo, formattedDate) : true

  return fromCheck && toCheck
}

function filterReconditioningEstCost(car, filters) {
  return !(
    car.reconditioningEstCost < filters.damageMin ||
    car.reconditioningEstCost > filters.damageMax
  )
}

function filterYear(car, filters) {
  return !(
    (filters.yearMin !== '' && car.year < filters.yearMin) ||
    (filters.yearMax !== '' && car.year > filters.yearMax)
  )
}

function filterQuery(car, filters) {
  const tokens =
    filters.query !== '' ? filters.query.trim().split(/[\s,]+/) : []

  return !(
    tokens.length &&
    (car?.title
      ? !tokens.every((token) => car.title.match(new RegExp(token, 'i')))
      : true) &&
    !tokens.some((token) => car.plate.match(new RegExp(token, 'i')))
  )
}

function filterPlateQuery(car, filters) {
  return !(
    filters.plateQuery !== '' &&
    !car.plate.match(new RegExp(filters.plateQuery.trim(), 'i'))
  )
}

function filterExists(car, filters, { ALL_OPTION, EXISTS_OK, EXISTS_MISSING }) {
  return !(
    filters.exists !== ALL_OPTION &&
    filters.exists !== (filters.existsCheck(car) ? EXISTS_OK : EXISTS_MISSING)
  )
}

function filterExistsPrice(
  car,
  filters,
  { ALL_OPTION, EXISTS_PRICE_OK, EXISTS_PRICE_MISSING }
) {
  return !(
    filters.existsPrice !== ALL_OPTION &&
    filters.existsPrice !==
      (car?.priceCheck ? EXISTS_PRICE_OK : EXISTS_PRICE_MISSING)
  )
}

function filterSoldType(car, filters, t, { ALL_OPTION }) {
  return !(
    filters.soldType !== ALL_OPTION &&
    filters.soldType !== t(`sessions.sold.status.${car.soldType}`)
  )
}

function filterstoragePlaceCheckState(car, filters, t, { ALL_OPTION }) {
  return (
    filters.storagePlaceCheckState === ALL_OPTION ||
    filters.storagePlaceCheckState ===
      t(`logistics.carStoragePlaceCheckState.${car.storagePlaceCheckState}`)
  )
}

function firstBlockFilter(
  car,
  filters,
  t,
  {
    ALL_OPTION,
    EXISTS_PRICE_OK,
    EXISTS_PRICE_MISSING,
    EXISTS_OK,
    EXISTS_MISSING,
  }
) {
  if (!filterKm(car, filters)) return false
  if (
    !filterDateRange(
      car?.storagePlaceDate,
      filters?.storagePlaceDateFrom,
      filters?.storagePlaceDateTo
    )
  )
    return false
  if (!filterReconditioningEstCost(car, filters)) return false
  if (!filterYear(car, filters)) return false
  if (!filterQuery(car, filters)) return false
  if (!filterPlateQuery(car, filters)) return false
  if (!filterExists(car, filters, { ALL_OPTION, EXISTS_OK, EXISTS_MISSING }))
    return false

  if (
    !filterExistsPrice(car, filters, {
      ALL_OPTION,
      EXISTS_PRICE_OK,
      EXISTS_PRICE_MISSING,
    })
  )
    return false

  if (
    !filterSoldType(car, filters, t, {
      ALL_OPTION,
    })
  )
    return false

  if (
    !filterstoragePlaceCheckState(car, filters, t, {
      ALL_OPTION,
    })
  )
    return false

  return true
}

function secondBlockFilter(car, filters, t, { ALL_OPTION }) {
  if (
    filters.soldType !== ALL_OPTION &&
    filters.soldType !== t(`sessions.sold.status.${car.soldType}`)
  )
    return false

  if (
    filters.category !== ALL_OPTION &&
    filters.category !== t(`carDetails.mapping.${car.vehicleCategory}`)
  )
    return false

  if (
    [car.soldPartnerName, ALL_OPTION].every(
      (partner) => !filters.partners.includes(partner)
    )
  )
    return false

  if (
    [car.storagePlace, ALL_OPTION].every(
      (storagePlace) => !filters.storagePlaces.includes(storagePlace)
    )
  )
    return false

  if ([car.brand, ALL_OPTION].every((brand) => !filters.brands.includes(brand)))
    return false

  if ([car.model, ALL_OPTION].every((model) => !filters.models.includes(model)))
    return false

  if (
    [mapLocation(car.location), ALL_OPTION].every(
      (location) => !filters.locations.includes(location)
    )
  )
    return false

  if ([car.fuelType, ALL_OPTION].every((fuel) => !filters.fuels.includes(fuel)))
    return false

  return true
}

function thirdBlockFilter(car, filters, t, { ALL_OPTION }) {
  const hasBlindPrice =
    car?.blindPrice !== null &&
    car?.blindPrice !== undefined &&
    car?.blindPrice !== ''
  const hasFirstChoice = !!car[FILTER_CAPS_FIRST_CHOICE]
  const hasSecondChoice = !!car[FILTER_CAPS_SECOND_CHOICE]
  const hasChoice = hasFirstChoice || hasSecondChoice || hasBlindPrice

  if (
    filters.caps &&
    (filters.caps === FILTER_CAPS_ALL_CHOICES
      ? !hasChoice
      : filters.caps === FILTER_CAPS_NO_CHOICES
      ? hasChoice
      : filters.caps === FILTER_CAPS_BLIND_OFFERS
      ? !hasBlindPrice
      : filters.caps === FILTER_CAPS_FIRST_CHOICE
      ? !hasFirstChoice
      : filters.caps === FILTER_CAPS_SECOND_CHOICE
      ? !hasSecondChoice
      : !car[filters.caps])
  )
    return false

  if (
    filters.assignmentCaps &&
    !checkRecapChoice(
      car,
      filters.assignmentPartners,
      filters.assignmentCaps !== FILTER_ASSIGNMENT_CAPS_NOT_CHOSEN
        ? filters.assignmentCaps
        : undefined
    )
  )
    return false

  if (
    filters.assignmentType &&
    !(filters.assignmentType === FILTER_ASSIGNMENT_TYPE_AUTOMATIC_CHOICE
      ? checkAutomaticAssignment(car, filters.assignmentPartners)
      : filters.assignmentType === FILTER_ASSIGNMENT_TYPE_TEAM_PROPOSAL_CHOICE
      ? checkTeamProposalAssignment(car, filters.assignmentPartners)
      : checkNoProposalAssignment(car, filters.assignmentPartners))
  )
    return false

  return true
}

const useCarsFilters = ({ startFilters, startDataFilters } = {}) => {
  const { t } = useTranslation()

  const ALL_OPTION = t('common.form.all')
  const EXISTS_MISSING = t('common.filters.carFoundMissing')
  const EXISTS_OK = t('common.filters.carFoundOk')
  const EXISTS_PRICE_MISSING = t('common.filters.priceMissing')
  const EXISTS_PRICE_OK = t('common.filters.priceOk')
  const CATEGORY_PRIVATE = t(`carDetails.mapping.PV`)
  const CATEGORY_COMMERCIAL = t(`carDetails.mapping.CV`)
  const SOLD_TYPE_SOLD = t(`sessions.sold.status.sold`)
  const SOLD_TYPE_PREASSIGNED = t(`sessions.sold.status.preAssigned`)
  const LOADER_STATES = LOADER_ALL_STATES.map((state) =>
    t(`logistics.carStoragePlaceCheckState.${state}`)
  )

  const defaultFilters = useMemo(
    () => ({
      kmMin: 0,
      kmMax: 0,
      damageMin: 0,
      damageMax: 0,
      yearMin: '',
      yearMax: '',
      storagePlaceDateFrom: null,
      storagePlaceDateTo: null,
      query: '',
      plateQuery: '',
      caps: '',
      assignmentCaps: '',
      assignmentType: '',
      carsBookedType: '',
      exists: ALL_OPTION,
      existsCheck: (car) => car.exists,
      existsPrice: ALL_OPTION,
      soldType: ALL_OPTION,
      assignmentPartners: '',
      partners: [ALL_OPTION],
      category: ALL_OPTION,
      storagePlaceCheckState: ALL_OPTION,
      storagePlaces: [ALL_OPTION],
      brands: [ALL_OPTION],
      models: [ALL_OPTION],
      locations: [ALL_OPTION],
      fuels: [ALL_OPTION],
      onlyFavorites: false,
      ...startFilters,
    }),
    [ALL_OPTION, startFilters]
  )

  const [baseFilters, setBaseFilters] = useState({ ...defaultFilters })

  const [filters, setFilters] = useState({
    ...defaultFilters,
    initialized: false,
  })

  const [filtersData, setFiltersData] = useState({
    kmMin: 0,
    kmMax: 0,
    damageMin: 0,
    damageMax: 0,
    yearMin: 0,
    yearMax: 0,
    exists: [ALL_OPTION, EXISTS_MISSING, EXISTS_OK],
    existsPrice: [ALL_OPTION, EXISTS_PRICE_MISSING, EXISTS_PRICE_OK],
    soldType: [ALL_OPTION, SOLD_TYPE_SOLD, SOLD_TYPE_PREASSIGNED],
    category: [ALL_OPTION, CATEGORY_PRIVATE, CATEGORY_COMMERCIAL],
    assignmentPartners: [''],
    partners: [ALL_OPTION],
    storagePlaceCheckState: [ALL_OPTION, ...LOADER_STATES],
    storagePlaces: [ALL_OPTION],
    brands: [ALL_OPTION],
    models: [ALL_OPTION],
    locations: [ALL_OPTION],
    fuels: [ALL_OPTION],
    ...startDataFilters,
  })

  const createAttributeList = useCallback(
    (list, attributeKey, mapFunction, acceptEmpty = false) => {
      return [
        ALL_OPTION,
        ...list
          .reduce(
            (previous, currentItem) =>
              (!acceptEmpty && !currentItem[attributeKey]) ||
              previous.includes(
                mapFunction
                  ? mapFunction(currentItem[attributeKey])
                  : currentItem[attributeKey]
              )
                ? previous
                : [
                    ...previous,
                    mapFunction
                      ? mapFunction(currentItem[attributeKey])
                      : currentItem[attributeKey],
                  ],
            []
          )
          .sort(),
      ]
    },
    [ALL_OPTION]
  )

  const initFilters = useCallback(
    (cars) => {
      const kms = cars.map((car) => car.km)
      const kmMin = Math.floor(Math.min(...kms) / 100) * 100
      const kmMax = Math.ceil(Math.max(...kms) / 100) * 100

      const damages = cars.map((car) => car.reconditioningEstCost)
      const damageMin = Math.floor(Math.min(...damages) / 100) * 100
      const damageMax = Math.ceil(Math.max(...damages) / 100) * 100

      const years = cars.map((car) => car.year)

      const storagePlaces = createAttributeList(cars, 'storagePlace')

      const brands = createAttributeList(cars, 'brand')

      const models = createAttributeList(cars, 'model')

      const locations = createAttributeList(cars, 'location', mapLocation)

      const fuels = createAttributeList(cars, 'fuelType')

      const partners = createAttributeList(cars, 'soldPartnerName')

      setBaseFilters((s) => ({
        ...s,
        kmMin,
        kmMax,
        damageMin,
        damageMax,
      }))
      setFilters((s) => ({
        ...s,
        kmMin,
        kmMax,
        damageMin,
        damageMax,
        initialized: true,
      }))

      setFiltersData((s) => ({
        ...s,
        kmMin,
        kmMax,
        damageMin,
        damageMax,
        yearMin: Math.min(...years),
        yearMax: Math.max(...years),
        partners,
        storagePlaces,
        brands,
        models,
        locations,
        fuels,
      }))
    },
    [createAttributeList]
  )

  const resetFilters = useCallback(() => {
    setFilters((s) => ({ ...s, ...baseFilters }))
  }, [baseFilters])

  const setKmRange = useCallback(
    (kmMin, kmMax) => {
      setFilters((s) => ({ ...s, kmMin, kmMax }))
    },
    [setFilters]
  )

  const setDamageRange = useCallback(
    (damageMin, damageMax) => {
      setFilters((s) => ({ ...s, damageMin, damageMax }))
    },
    [setFilters]
  )

  const setYearRange = useCallback(
    (yearMin, yearMax) => {
      setFilters((s) => ({ ...s, yearMin, yearMax }))
    },
    [setFilters]
  )

  const setStoragePlaceDateRange = useCallback(
    (storagePlaceDateFrom, storagePlaceDateTo) => {
      setFilters((s) => ({
        ...s,
        storagePlaceDateFrom,
        storagePlaceDateTo,
      }))
    },
    [setFilters]
  )

  const setExists = useCallback(
    (value) => {
      setFilters((s) => ({ ...s, exists: value }))
    },
    [setFilters]
  )

  const setExistsPrice = useCallback(
    (value) => {
      setFilters((s) => ({ ...s, existsPrice: value }))
    },
    [setFilters]
  )

  const setSoldType = useCallback(
    (value) => {
      setFilters((s) => ({ ...s, soldType: value }))
    },
    [setFilters]
  )

  const setCategory = useCallback(
    (value) => {
      setFilters((s) => ({ ...s, category: value }))
    },
    [setFilters]
  )

  const setPartners = useCallback(
    (value) => {
      setFilters((s) => ({ ...s, partners: value }))
    },
    [setFilters]
  )

  const setAssignmentPartners = useCallback(
    (value) => {
      setFilters((s) => ({ ...s, assignmentPartners: value }))
    },
    [setFilters]
  )

  const setStoragePlaces = useCallback(
    (value) => {
      setFilters((s) => ({ ...s, storagePlaces: value }))
    },
    [setFilters]
  )

  const setStoragePlaceCheckState = useCallback(
    (value) => {
      setFilters((s) => ({ ...s, storagePlaceCheckState: value }))
    },
    [setFilters]
  )

  const setBrands = useCallback(
    (value) => {
      setFilters((s) => ({ ...s, brands: value }))
    },
    [setFilters]
  )

  const setModels = useCallback(
    (value) => {
      setFilters((s) => ({ ...s, models: value }))
    },
    [setFilters]
  )

  const setLocations = useCallback(
    (value) => {
      setFilters((s) => ({ ...s, locations: value }))
    },
    [setFilters]
  )

  const setFuels = useCallback(
    (value) => {
      setFilters((s) => ({ ...s, fuels: value }))
    },
    [setFilters]
  )

  const setQuery = useCallback(
    (value) => {
      setFilters((s) => ({ ...s, query: value }))
    },
    [setFilters]
  )

  const setPlateQuery = useCallback(
    (value) => {
      setFilters((s) => ({ ...s, plateQuery: value }))
    },
    [setFilters]
  )

  const setCaps = useCallback(
    (value) => {
      if (value === filters.caps) {
        return
      }
      setFilters((s) => ({
        ...s,
        caps: value,
      }))
    },
    [filters.caps]
  )

  const setAssignmentCaps = useCallback(
    (value) => {
      if (value === filters.assignmentCaps) {
        return
      }
      setFilters((s) => ({
        ...s,
        assignmentCaps: value,
      }))
    },
    [filters.assignmentCaps]
  )

  const setAssignmentType = useCallback(
    (value) => {
      if (value === filters.assignmentType) {
        return
      }
      setFilters((s) => ({
        ...s,
        assignmentType: value,
      }))
    },
    [filters.assignmentType]
  )

  const setCarsBookedType = useCallback(
    (value) => {
      if (value === filters.carsBookedType) {
        return
      }
      setFilters((s) => ({
        ...s,
        carsBookedType: value,
      }))
    },
    [filters.carsBookedType]
  )

  const setOnlyFavorites = useCallback(
    (value) => {
      setFilters((s) => ({
        ...s,
        onlyFavorites: value,
      }))
    },
    [setFilters]
  )

  const filterCars = useCallback(
    (cars, filters) => {
      return cars.filter((car) => {
        if (
          !firstBlockFilter(car, filters, t, {
            ALL_OPTION,
            EXISTS_PRICE_OK,
            EXISTS_PRICE_MISSING,
            EXISTS_OK,
            EXISTS_MISSING,
          })
        )
          return false

        if (
          !secondBlockFilter(car, filters, t, {
            ALL_OPTION,
          })
        )
          return false

        if (
          !thirdBlockFilter(car, filters, t, {
            ALL_OPTION,
          })
        )
          return false

        if (
          filters.carsBookedType &&
          !([
            FILTER_CARS_BOOKED_TYPE_FIRST_CHOICE,
            FILTER_CARS_BOOKED_TYPE_SECOND_CHOICE,
          ].includes(filters.carsBookedType)
            ? filters.carsBookedType === FILTER_CARS_BOOKED_TYPE_FIRST_CHOICE
              ? !!car?.firstChoice
              : hasCarsBookedBlindPlusChoice(car)
            : getCarsBookedDisplayStatus(car) === filters.carsBookedType)
        )
          return false

        if (filters.onlyFavorites && !car.favorite) return false

        return true
      })
    },
    [
      ALL_OPTION,
      EXISTS_MISSING,
      EXISTS_OK,
      EXISTS_PRICE_MISSING,
      EXISTS_PRICE_OK,
      t,
    ]
  )

  return {
    filters,
    filtersData,
    initFilters,
    setKmRange,
    setDamageRange,
    setYearRange,
    setStoragePlaceDateRange,
    setExists,
    setExistsPrice,
    setSoldType,
    setPartners,
    setAssignmentPartners,
    setStoragePlaces,
    setStoragePlaceCheckState,
    setBrands,
    setModels,
    setLocations,
    setFuels,
    setCategory,
    setQuery,
    setPlateQuery,
    setCaps,
    setAssignmentCaps,
    setAssignmentType,
    setCarsBookedType,
    setOnlyFavorites,
    filterCars,
    resetFilters,
  }
}

export default useCarsFilters
